import React from 'react';
import { useInterval } from 'usehooks-ts';

import * as Elevated from '@pitaman71/omniglot-introspect';

import { Annotations, Clients, Dialogues, Inspectors, Objects, Properties, Relations, Stores, Streams } from '@pitaman71/omniglot-live-data';

import { makeStyles, Box, Button, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@material-ui/core';
import * as MuiIcons from '@material-ui/icons';

import * as ViewDiscussion from './ViewDiscussion';

import * as Debug from '../Debug';

const __moduleName__ = 'omniglot-live-react.MaterialUi.ViewDialogue';

export function Case<DialogueTypeParams extends Dialogues.TypeParams>(props: {
    builder: BaseBuilder<DialogueTypeParams>;
    descriptor: Dialogues.Case<DialogueTypeParams["Binding"]>,
}) {
    const [ condition, setCondition ] = React.useState(props.descriptor.condition(props.builder._instance));
    const [ enable, setEnable ] = React.useState<boolean|undefined>();
    const [ status, setStatus ] = React.useState<Streams.Status>({ initializing: true })
    const [ subscription, setSubscription ] = React.useState<Streams.Subscriptions|undefined>();

    const client = Clients.ScalarClient.fromDelta<boolean, Elevated.Domain<boolean>>(`${__moduleName__}.Case`, setEnable, setStatus);
    React.useEffect(() => {
        if(subscription) {
            subscription.unsubscribe();
        }
        setSubscription(client.watchParent(condition));
        return (() => {
            if(subscription) {
                subscription.unsubscribe();
            }    
        })
    }, [ condition ])

    React.useEffect(() => {
        setCondition(props.descriptor.condition(props.builder._instance));
    }, [ props.builder ]);

    const [ elements, setElements ] = React.useState<JSX.Element[]>([]);

    React.useEffect(() => {
        if(!enable) {
            setElements([])
        } else {
            const builder_ = props.builder.clone();
            props.descriptor.then(builder_);
            setElements(builder_.elements);
        }
    }, [ enable, props.builder ]);

    return (
        <React.Fragment>
            <div style={{ display: "none"}}>{`BEGIN Case ${condition}`}</div>
            {elements}
            <div style={{ display: "none"}}>{`END   Case ${condition}`}</div>
        </React.Fragment>
    );
}
    
function Cue(props: {
    cue: Dialogues.StringStream,
}) {
    return (
        <React.Fragment>
            <StringStream stream={props.cue} render={(text:string|undefined) => <span>{text}</span>}/>
        </React.Fragment>
    )
}

function Paragraph(props: {
    stream: Dialogues.StringStream,
}) {
    return (
        <React.Fragment>
            <p><StringStream stream={props.stream} render={(text:string|undefined) => <span>{text}</span>}/></p>
        </React.Fragment>
    )
}

export function Icon(props: {
    makeAnnotations: Annotations.Maker<JSX.Element>,
    from?: Dialogues.Descriptor<any>,
    via?: Relations.Descriptor<any>,
    dialogue?: Dialogues.Instance<any>,
    control?: any
}) {
    if(props.dialogue) {
        return (
            <Box display="flex">
                {
                    props.makeAnnotations.dialogue(props.dialogue.descriptor).icon || <MuiIcons.ErrorOutlined/>
                }
            </Box>
        );    
    } else if(props.control) {
        return (
            <Box display="flex">
                {
                    props.makeAnnotations.control(props.control).icon || <MuiIcons.ErrorOutlined/>
                }
            </Box>
        );    
    }
    return (
        <Box display="flex">
            {
                <MuiIcons.ErrorOutlined/>
            }
        </Box>
    );    
}

export function Label(props: {
    makeAnnotations: Annotations.Maker<JSX.Element>,
    dialogue?: Dialogues.Instance<any>,
    control?: any
}) {
    if(props.dialogue) {
        return props.makeAnnotations.dialogue(props.dialogue.descriptor).label || <span>?</span>;
    } else if(props.control) {
        return (
            <Box display="flex">
                {
                    props.makeAnnotations.control(props.control).label || <MuiIcons.ErrorOutlined/>
                }
            </Box>
        );    
    }
    return (
        <Box display="flex">
            {
                <MuiIcons.ErrorOutlined/>
            }
        </Box>
    );    
}

export function Headline<DialogueTypeParams extends Dialogues.TypeParams>(props: {
    instance: Dialogues.Instance<DialogueTypeParams>
}) {
    const discussionState = React.useContext(ViewDiscussion.ReactContext);
    const panel = discussionState?.top();
    if(!panel) return (
        <Debug.Boundary name={`${__moduleName__}.Headline`}>
            <Debug.Error message={`Missing/empty discussionState`}/>
        </Debug.Boundary>
    );

    let elements: JSX.Element[] = [];
    const builder = new class _Builder extends BaseBuilder<DialogueTypeParams> {
        clone(): BaseBuilder<DialogueTypeParams> {
            return new _Builder(props.instance, panel.zone);
        }

        headline(cue: Dialogues.StringStream): void {
            this._elements.push(<StringStream stream={cue} render={(text) => <span>{text}</span>}/>);
        }    
    }(props.instance, panel.zone);
    props.instance.descriptor.build(builder);
    elements = builder.elements;

    return (
        <React.Fragment>
            <div style={{ display: "none"}}>{`BEGIN Headline ${props.instance}`}</div>
            {elements}
            <div style={{ display: "none"}}>{`END   Headline ${props.instance}`}</div>
        </React.Fragment>
    );
}

export function Status<DialogueTypeParams extends Dialogues.TypeParams>(props: {
    instance: Dialogues.Instance<DialogueTypeParams>
}) {
    const discussionState = React.useContext(ViewDiscussion.ReactContext);

    const [ statusStream, setStatusStream ] = React.useState<undefined|Streams.ValueStream<DialogueTypeParams["Status"]>>();
    const [ value, setValue] = React.useState<undefined|DialogueTypeParams["Status"]>();
    const [ status, setStatus ] = React.useState<Streams.Status>({ initializing: true })

    const client = Clients.ScalarClient.fromDelta<DialogueTypeParams["Status"], Elevated.Domain<DialogueTypeParams["Status"]>>(`${__moduleName__}/Status`, setValue, setStatus);
    const [ subscription, setSubscription ] = React.useState<Streams.Subscriptions|undefined>();

    React.useEffect(() => {
        if(!statusStream?.scalar)
            setSubscription(undefined);
        else
            setSubscription(client.watchParent(statusStream.scalar));
        return (() => {
            if(subscription) {
                subscription.unsubscribe();
            }    
        })
    }, [ statusStream ])

    React.useEffect(() => {
        const panel = discussionState?.top();
        if(panel) {
            props.instance.descriptor.build(new class _Builder extends BaseBuilder<DialogueTypeParams> {
                clone(): BaseBuilder<DialogueTypeParams> {
                    return new _Builder(this._instance, panel.zone);
                }
                status(value_: Streams.ValueStream<DialogueTypeParams["Status"]>): void {
                    setStatusStream(value_);
                }

            }(props.instance, panel.zone));
        }
    }, [ props.instance ]);

    const panel = discussionState?.top();
    if(!panel) return (
        <Debug.Boundary name={`${__moduleName__}.Status`}>
            <Debug.Error message={`Missing/empty discussionState`}/>
        </Debug.Boundary>
    );

    if(value === 'complete') {
        return <IconButton>{<MuiIcons.CheckBoxOutlined/>}</IconButton>
    } else if(value === 'incomplete') {
        return <IconButton>{<MuiIcons.NotificationsActiveOutlined/>}</IconButton>
    }
    return (
        <Debug.Boundary name={`${__moduleName__}.Status`}>
            <Debug.Error message={`Illegal mode status=${value}`}/>
        </Debug.Boundary>
    );
}

function Property<PropertyTypeParams extends Properties.TypeParams>(props: {
    stream: Streams.PropertyStream<PropertyTypeParams>,
    makeInspectors: Inspectors.Maker<JSX.Element>,
}) {
    const makeInspector = props.makeInspectors.property(props.stream.descriptor);
    if(props.stream.scalar) {
        return makeInspector.scalar(props.stream.scalar);
    } else if(props.stream.set) {
        return makeInspector.set(props.stream.set);
    } else if(props.stream.sequence) {
        return makeInspector.sequence(props.stream.sequence);
    } else if(props.stream.map) {
        return makeInspector.map(props.stream.map);
    }
    return (
        <Debug.Boundary name={`${__moduleName__}.Property`}>
            <Debug.Error message={`Malformed stream ${props.stream}`}/>
        </Debug.Boundary>
    );
}

function Relation<RelationBindingType extends Objects.BindingType>(props: {
    stream: Streams.RelationStream<RelationBindingType>,
    makeInspectors: Inspectors.Maker<JSX.Element>,
}) {
    const makeInspector = props.makeInspectors.relation(props.stream.anchor.descriptor);
    return (
        <Debug.Boundary name={`${__moduleName__}.Relation`}>
            {makeInspector.out(props.stream)}
        </Debug.Boundary>
    )
}

function StringStream(props: {
    stream: Dialogues.StringStream,
    render: (text: string|undefined) => JSX.Element
}) {
    const domain = props.stream.domain;
    const elementRef = React.useRef<HTMLDivElement>(null);
    const [ value, setValue ] = React.useState<string|undefined>(undefined);
    const [ status, setStatus ] = React.useState<Streams.Status>({ initializing: true })
    const [ error, setError ] = React.useState<string|undefined>(undefined);

    const client = Clients.ScalarClient.fromDelta<string, Elevated.Domain<string>>(`${__moduleName__}/Say(${props.stream.persistKey})`, setValue, setStatus);
    const [ subscription, setSubscription ] = React.useState<Streams.Subscriptions|undefined>();
    React.useEffect(() => {
        if(subscription) {
            subscription.unsubscribe();
        }
        setSubscription(client.watchParent(props.stream));
        return (() => {
            if(subscription) {
                subscription.unsubscribe();
            }    
        })
    }, [ ])
    return props.render(value);
}

export abstract class BaseBuilder<DialogueTypeParams extends Dialogues.TypeParams> extends Dialogues.Builder<DialogueTypeParams> {
    _instance: Dialogues.Instance<DialogueTypeParams>;
    _zone: Stores.Zone;
    protected _elements: JSX.Element[] = [];

    constructor(
        instance: Dialogues.Instance<DialogueTypeParams>,
        zone: Stores.Zone
    ) {
        super();
        this._instance = instance;
        this._zone = zone;
    }

    instance() { return this._instance }
    zone() { return this._zone }
    streams() { return this._zone.streams() }

    abstract clone(): BaseBuilder<DialogueTypeParams>;

    get elements() { return this._elements }
    set elements(elements_) { this._elements = elements_ }
    
    when(case_: Dialogues.Case<DialogueTypeParams["Binding"]>): void {
        this._elements.push(<Case descriptor={case_} builder={this}/>);
    }
}

function PushToOpen<DialogueTypeParams extends Dialogues.TypeParams>(props: {
    instance: Dialogues.Instance<DialogueTypeParams>
}) {
    const discussionState = React.useContext(ViewDiscussion.ReactContext);
    const panel = discussionState?.top();
    if(!panel) return (
        <Debug.Boundary name={`${__moduleName__}.PushToOpen`}>
            <Debug.Error message={`Missing/empty discussionState`}/>
        </Debug.Boundary>
    );

    return (
        <Debug.Boundary name={`${__moduleName__}.PushToOpen`}>
            <Button disabled={!discussionState} onClick={ !discussionState ? undefined : (event: any) => {
                    discussionState.panels().open(props.instance);
            }}>                
                <Headline instance={props.instance}/>
            </Button>
        </Debug.Boundary>
    )
}

export function Entrances<DialogueTypeParams extends Dialogues.TypeParams>(props: {
    instance: Dialogues.Instance<DialogueTypeParams>,
    makeAnnotations: Annotations.Maker<JSX.Element>
}) {
    let elements: JSX.Element[] = [];
    const discussionState = React.useContext(ViewDiscussion.ReactContext);
    const panel = discussionState?.top();
    if(!panel) return (
        <Debug.Boundary name={`${__moduleName__}.Entrances`}>
            <Debug.Error message={`Missing/empty discussionState`}/>
        </Debug.Boundary>
    );

    const builder = new class _Builder extends BaseBuilder<DialogueTypeParams> {
        clone(): BaseBuilder<DialogueTypeParams> {
            return new _Builder(this._instance, panel.zone);
        }
        control(options: {
            name: DialogueTypeParams["Control"],
            isEntrance?: boolean,
            isExit?: boolean,
            enable?: (binding: DialogueTypeParams["Binding"]) => Streams.ScalarStream<boolean, Elevated.Domain<boolean>>,
            action?: (binding: DialogueTypeParams["Binding"]) => Promise<any>,
        }): void {
            if(options.isEntrance) {
                this._elements.push(
                    <Trigger 
                        disabled={false}
                        execute={ () => discussionState && discussionState.panels().open(props.instance) }
                        label={<Label control={options.name} {...props}/>}
                        endIcon={<Icon control={options.name} {...props}/>}
                        intervalMs={150}
                        holdTimeMs={2200}
                    />
                );
            }
        }    
    }(props.instance, panel.zone);
    props.instance.descriptor.build(builder);
    elements = builder.elements;
    return <Debug.Boundary name={`${__moduleName__}.Entrances`}>{elements}</Debug.Boundary>
}

export function AsTableRow(props: {
    from?: Dialogues.Descriptor<any>,
    via?: Relations.Descriptor<any>,
    dialogue: Dialogues.Instance<any>,
    makeAnnotations: Annotations.Maker<JSX.Element>
}) {
    return (
        <Debug.Boundary name={`${__moduleName__}.AsTableRow`}>
            <tr>
                <td>
                    <Status {...props} instance={props.dialogue}/>
                </td>
                <td>
                    <Headline {...props} instance={props.dialogue}/>
                </td>
                <td>
                    <Entrances {...props} instance={props.dialogue}/>
                </td>
            </tr>
        </Debug.Boundary>
    );
}

function Trigger(props: {
    intervalMs: number,
    holdTimeMs: number;
    disabled: boolean,
    label?: JSX.Element,
    startIcon?: JSX.Element,
    endIcon?: JSX.Element,
    execute: () => void,
    confirmation?: Dialogues.StringStream,
}) {
    const [ holdStart, setHoldStart ] = React.useState<undefined|Date>();
    const [ progress, setProgress ] = React.useState<number>(0.0);
    const [ mode, setMode ] = React.useState<'normal'|'tapped'>('normal');

    const classes = makeStyles((theme) => ({
        wrapper: {
          margin: theme.spacing(1),
          position: 'relative',
          display: 'inline'
        },
        buttonProgress: {
          position: 'absolute',
          top: '50%',
          left: '50%',
          marginTop: -12,
          marginLeft: -12,
        },
      }))();

    useInterval(() => {
        const now = new Date();
        if(holdStart !== undefined) {
            const ms = (now.getTime() - holdStart.getTime());
            if(ms > props.holdTimeMs) {
                props.execute();
            }
            setProgress(ms / props.holdTimeMs)
        }
    }, props.intervalMs);
    if(props.confirmation === undefined) {
        return (
            <Button {...props} startIcon={props.startIcon} endIcon={props.endIcon} key="trigger" onClick={(event: any) => { props.execute() }}>{props.label}</Button>
        );
    } else {
        return (
            <React.Fragment>
                <Dialog key="confirmation" open={mode === 'tapped'} onClose={() => setMode('normal')}>
                <Container>
                    <DialogTitle>Press and hold to:</DialogTitle>
                    <DialogContent>
                        <StringStream stream={props.confirmation} render={(item) => <p>{item}</p>}/>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setMode('normal')}>Dismiss</Button>
                    </DialogActions>
                </Container>
                </Dialog>
                <div className={classes.wrapper}>
                    <Button {...props}
                        startIcon={props.startIcon}
                        endIcon={props.endIcon}
                        key="trigger"
                        onMouseDownCapture={(event: any) => {
                            setHoldStart(new Date());
                        }}                
                        onMouseUpCapture={(event: any) => {
                            setHoldStart(undefined);
                            setProgress(0.0);
                            if(progress < 0.2) setMode('tapped');
                        }}                
                    >
                        {props.label}
                    </Button>
                    {progress > 0.0 && 
                        <CircularProgress 
                            size={24} variant="determinate" 
                            className={classes.buttonProgress} 
                            value={progress * 100}
                            onMouseUpCapture={(event: any) => {
                                setHoldStart(undefined);
                                setProgress(0.0);
                                if(progress < 0.2) setMode('tapped');
                            }}                
                        />
                    }
                </div>
            </React.Fragment>
        );
    }
}
function Control<DialogueTypeParams extends Dialogues.TypeParams, ControlType>(props: {
    instance: Dialogues.Instance<DialogueTypeParams>,
    name: ControlType,
    isEntrance?: boolean,
    isExit?: boolean,
    enable?: (binding: DialogueTypeParams["Binding"], state: DialogueTypeParams["State"]) => Streams.ScalarStream<boolean, Elevated.Domain<boolean>>,
    confirmation?: Dialogues.StringStream,
    action?: (binding: DialogueTypeParams["Binding"], state: DialogueTypeParams["State"]) => Promise<any>,
    makeAnnotations: Annotations.Maker<JSX.Element>
}): JSX.Element {
    const discussionState = React.useContext(ViewDiscussion.ReactContext);
    const [ enable, setEnable ] = React.useState<boolean|undefined>(props.enable === undefined ? true : undefined);
    const [ status, setStatus ] = React.useState<Streams.Status>({ initializing: true })
    const [ subscription, setSubscription ] = React.useState<Streams.Subscriptions|undefined>();

    const client = Clients.ScalarClient.fromDelta<boolean, Elevated.Domain<boolean>>(`${__moduleName__}.Enable`, setEnable, setStatus);
    React.useEffect(() => {
        if(props.enable === undefined) {
            setEnable(true);
            setSubscription(undefined);
        } else {
            setSubscription(client.watchParent(props.enable(props.instance.binding, props.instance.state)));
        }
        return (() => {
            if(subscription) {
                subscription.unsubscribe();
            }    
        })
    }, [ props.instance.binding, props.instance.state, props.enable ])

    return (
        <Trigger 
            disabled={!enable} 
            execute={ () => {
                const done = props.action ? props.action(props.instance.binding, props.instance.state) : Promise.resolve();
                done.then(() => {
                    if(props.isExit && discussionState) discussionState.panels().close();
                })
            } }
            label={<Label control={props.name} {...props}/>}
            startIcon={<Icon control={props.name} {...props}/>}
            confirmation={props.confirmation}
            intervalMs={150}
            holdTimeMs={2200}
        />
    );
}

export class PanelGenerator<DialogueTypeParams extends Dialogues.TypeParams> extends BaseBuilder<DialogueTypeParams>  {
    listElements: JSX.Element[] = [];
    makeAnnotations: Annotations.Maker<JSX.Element>;
    makeInspectors: Inspectors.Maker<JSX.Element>;

    constructor(
        makeAnnotations: Annotations.Maker<JSX.Element>,
        makeInspectors: Inspectors.Maker<JSX.Element>,
        instance: Dialogues.Instance<DialogueTypeParams>,
        zone: Stores.Zone
    ) {
        super(instance, zone)
        this.makeAnnotations = makeAnnotations;
        this.makeInspectors = makeInspectors;
    }

    begin() {
        this._elements.push(
            <div style={{ display: "none" }}>{`BEGIN ${__moduleName__}.PanelGenerator ${this.instance}`}</div>
        )
    }

    end() {
        this._elements.push(
            <div style={{ display: "none" }}>{`END   ${__moduleName__}.PanelGenerator ${this.instance}`}</div>
        )
    }
    
    get elements() { this._flushList(); return this._elements; }
    set elements(elements_) { this._elements = elements_ }
    
    private _flushList() {
        if(this.listElements.length > 0) {
            this._elements = [
                ...this._elements, 
                (
                    <Box display="flex" justifyContent="center" flexDirection="row">
                        <table>
                            <tbody>{this.listElements}</tbody>
                        </table>
                    </Box>
                )
            ];
            this.listElements = [];
        }
    }

    clone(): BaseBuilder<DialogueTypeParams> {
        this._flushList();
        return new PanelGenerator(this.makeAnnotations, this.makeInspectors, this._instance, this._zone);
    }

    headline(cue: Dialogues.StringStream, why?: Dialogues.StringStream, ...details: Dialogues.StringStream[]): void {
        this._flushList();
        this._elements.push(
            <StringStream stream={cue} render={element => <h1>{element}</h1>}></StringStream>
        )
    } 

    route<RouteTypeParams extends Dialogues.TypeParams>(
        forward: DialogueTypeParams["Direction"],
        descriptor: Dialogues.Descriptor<RouteTypeParams>, 
        makeContext: (binding: RouteTypeParams["Binding"]) => Dialogues.Instance<RouteTypeParams>,
        backward?: RouteTypeParams["Direction"]
    ): void {
        const instance_ = makeContext(this._instance.binding)
        this.listElements.push(
            <AsTableRow dialogue={instance_} makeAnnotations={this.makeAnnotations}/>
        );
    }

    paragraphs(...text: Dialogues.StringStream[]): void {
        this._flushList();
        this._elements.push(
            <React.Fragment>
                { text.map(stream => <Paragraph stream={stream}/>)}
            </React.Fragment>
        )
    }

    detour<DetourTypeParams extends Dialogues.TypeParams>(
        descriptor: Dialogues.Descriptor<DetourTypeParams>, 
        makeContext: (binding: DialogueTypeParams["Binding"], state: DialogueTypeParams["State"]) => Dialogues.Instance<DetourTypeParams>
    ): void {
        this._flushList();
        const instance_ = makeContext(this._instance.binding, this._instance.state)
        this._elements.push(
            <PushToOpen instance={instance_}/>
        );
    }

    property<PropertyTypeParams extends Properties.TypeParams>(
        stream: Streams.PropertyStream<PropertyTypeParams>
    ): void {
        this._flushList();
        this._elements.push(<Property makeInspectors={this.makeInspectors} stream={stream}/>)
    }
    relation<RelationBindingType extends Objects.BindingType>(
        stream: Streams.RelationStream<RelationBindingType>
    ): void {
        this._flushList();
        this._elements.push(<Relation makeInspectors={this.makeInspectors} stream={stream}/>)
    }

    control(options: {
        name: DialogueTypeParams["Control"],
        isEntrance?: boolean,
        isExit?: boolean,
        enable?: (binding: DialogueTypeParams["Binding"], state: DialogueTypeParams["State"]) => Streams.ScalarStream<boolean, Elevated.Domain<boolean>>,
        action?: (binding: DialogueTypeParams["Binding"], state: DialogueTypeParams["State"]) => Promise<any>,
    }): void {
        this._flushList();
        if(!options.isEntrance) {
            this._elements.push(<Control {...options} makeAnnotations={this.makeAnnotations} instance={this._instance}/>)
        }
    }

    review(cue: Dialogues.StringStream): void {
        this._flushList();
        this._elements.push(<Cue cue={cue}/>);
    }
}
